import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { 
  attemptTokenRefresh, 
  getTokenStatus, 
  proactiveTokenRefresh,
  isTokenExpiringSoon 
} from '@/lib/auth/token-validation';

export function useSessionWarning() {
  const warningShownRef = useRef(false);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [tokenStatus, setTokenStatus] = useState(() => {
    // 클라이언트 사이드에서만 초기 상태 설정
    if (typeof window !== 'undefined') {
      return getTokenStatus();
    }
    return {
      hasToken: false,
      isExpired: false,
      isExpiringSoon: false,
      timeUntilExpiry: null,
      minutesUntilExpiry: null
    };
  });

  useEffect(() => {
    // 클라이언트 사이드에서만 실행
    if (typeof window === 'undefined') {
      return;
    }

    const checkTokenExpiry = async () => {
      try {
        // 토큰 상태 업데이트
        const currentStatus = getTokenStatus();
        setTokenStatus(currentStatus);

        // 토큰이 없으면 체크 중단
        if (!currentStatus.hasToken) return;

        // 예측적 갱신 시도 (15분 전)
        if (isTokenExpiringSoon(15) && !warningShownRef.current) {
          console.log('[Session Warning] Attempting proactive refresh...');
          const refreshSuccess = await proactiveTokenRefresh(15);
          
          if (refreshSuccess) {
            console.log('[Session Warning] Proactive refresh successful');
            return; // 갱신 성공 시 경고 표시하지 않음
          }
        }

        // 만료 10분 전에 경고 표시
        if (currentStatus.isExpiringSoon && currentStatus.minutesUntilExpiry && currentStatus.minutesUntilExpiry <= 10 && !warningShownRef.current) {
          warningShownRef.current = true;
          
          toast.custom((t) => {
            const minutes = currentStatus.minutesUntilExpiry;
            return (
              <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
                <div className="flex-1 w-0 p-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 pt-0.5">
                      <svg className="h-10 w-10 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        Session Expiring Soon
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Your session will expire in {minutes} minutes.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex space-x-2">
                    <button
                      onClick={async () => {
                        const success = await attemptTokenRefresh();
                        if (success) {
                          toast.success('Session extended successfully!');
                          warningShownRef.current = false;
                        } else {
                          toast.error('Failed to extend session');
                        }
                        toast.dismiss(t.id);
                      }}
                      className="flex-1 bg-indigo-600 text-white text-sm font-medium py-2 px-3 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Extend Session
                    </button>
                    <button
                      onClick={() => toast.dismiss(t.id)}
                      className="flex-1 bg-gray-200 text-gray-800 text-sm font-medium py-2 px-3 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            );
          }, {
            duration: 10000,
            position: 'top-right',
          });
        }

        // 만료 1분 전에 최종 경고
        if (currentStatus.minutesUntilExpiry && currentStatus.minutesUntilExpiry <= 1 && currentStatus.minutesUntilExpiry > 0) {
          toast.error('Session expiring in less than 1 minute!', {
            duration: 5000,
            position: 'top-center',
          });
        }
      } catch (error) {
        console.error('Session warning check error:', error);
      }
    };

    // 1분마다 체크
    checkIntervalRef.current = setInterval(checkTokenExpiry, 60 * 1000);
    
    // 초기 체크
    checkTokenExpiry();

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  return {
    resetWarning: () => {
      warningShownRef.current = false;
    }
  };
} 